import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Bot, User, Trash2, CheckCircle2 } from 'lucide-react';
import { getAuthHeaders } from '@/services/authConfig';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import ReactMarkdown from 'react-markdown';

interface AgentChatProps {
  agentId: string;
  agentName?: string;
  agentContent?: string;
  mode?: 'full' | 'compact';
  authMode?: 'session' | 'token';
  embedToken?: string;
  className?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

interface AgentInfo {
  name: string;
  model: string;
}

interface SSEResult {
  contents: string[];
  error?: string;
  agentInfo?: AgentInfo;
}

function parseSSELines(text: string): SSEResult {
  const lines = text.split('\n');
  const contents: string[] = [];
  let error: string | undefined;
  let agentInfo: AgentInfo | undefined;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const payload = trimmed.slice(5).trim();
    if (payload === '[DONE]') continue;
    try {
      const parsed = JSON.parse(payload);
      if (parsed.type === 'error' && parsed.message) {
        error = parsed.message;
      } else if (parsed.type === 'agent_info') {
        agentInfo = { name: parsed.name, model: parsed.model };
      } else if (parsed.content) {
        contents.push(parsed.content);
      }
    } catch {
      // Not valid JSON, skip
    }
  }
  return { contents, error, agentInfo };
}

export function AgentChat({
  agentId,
  agentName,
  agentContent,
  mode = 'full',
  authMode = 'session',
  embedToken,
  className = '',
}: AgentChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: 'Welcome! How can I help?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeAgent, setActiveAgent] = useState<AgentInfo | null>(null);
  const [sessionId] = useState(() => crypto.randomUUID());
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleClear = useCallback(() => {
    setMessages([
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Welcome! How can I help?',
        timestamp: new Date(),
      },
    ]);
    setInput('');
    setIsStreaming(false);
    setActiveAgent(null);
  }, []);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    const assistantMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput('');
    setIsStreaming(true);

    try {
      let response: Response;

      if (authMode === 'token' && embedToken) {
        response = await fetch('/api/embed/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Embed-Token': embedToken,
          },
          credentials: 'include',
          body: JSON.stringify({
            agent_name: agentId,
            message: trimmed,
            session_id: sessionId,
          }),
        });
      } else {
        response = await fetch('/api/playground/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          credentials: 'include',
          body: JSON.stringify({
            agent_id: agentId,
            messages: [{ role: 'user', content: trimmed }],
            agent_content: agentContent,
          }),
        });
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const { contents, error: sseError, agentInfo: info } = parseSSELines(chunk);

        if (info) {
          setActiveAgent(info);
        }

        if (sseError) {
          accumulated = `Error: ${sseError}`;
          break;
        }

        if (contents.length > 0) {
          accumulated += contents.join('');
          const currentAccumulated = accumulated;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessage.id
                ? { ...msg, content: currentAccumulated }
                : msg
            )
          );
        }
      }

      // Finalize the message
      const finalContent = accumulated;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessage.id
            ? { ...msg, content: finalContent || 'No response received.', isStreaming: false }
            : msg
        )
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessage.id
            ? { ...msg, content: `Error: ${errorMessage}`, isStreaming: false }
            : msg
        )
      );
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, agentId, agentContent, authMode, embedToken, sessionId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const isCompact = mode === 'compact';

  return (
    <Card className={`flex flex-col ${isCompact ? 'min-h-[350px]' : 'min-h-[500px]'} ${className}`}>
      {!isCompact && (
        <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="h-5 w-5" />
              {agentName || 'Agent Chat'}
            </CardTitle>
            {activeAgent && (
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-xs text-green-600 border-green-600 gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {activeAgent.name}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {activeAgent.model}
                </Badge>
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={handleClear} disabled={isStreaming}>
            <Trash2 className="h-4 w-4 mr-1" />
            Clear Chat
          </Button>
        </CardHeader>
      )}

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <ScrollArea className="flex-1 px-4 py-2" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex gap-2 max-w-[80%] ${
                    msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                      msg.role === 'user'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <User className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                  </div>
                  <div
                    className={`rounded-lg px-3 py-2 ${isCompact ? 'text-sm' : 'text-base'} ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    {msg.isStreaming && (
                      <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="border-t p-3 flex gap-2 items-end">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Shift+Enter for newline)"
            disabled={isStreaming}
            className={`resize-none ${isCompact ? 'text-sm min-h-[36px]' : 'min-h-[44px]'}`}
            rows={1}
          />
          <Button
            onClick={sendMessage}
            disabled={isStreaming || !input.trim()}
            size={isCompact ? 'sm' : 'default'}
          >
            {isStreaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default AgentChat;
